/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { getApolloContext } from '@apollo/client';
import {
  isRecsDebugActive,
  pickExplainerHostContext,
  useRecommendationsExplainerEnabled,
} from '@coursera-web/recommendations-common';
import type { RecommendationsExplainerInput } from '@coursera-web/recommendations-common';

import { IconButton } from '@coursera/cds-core';

import { LazyRecommendationsExplainerDialog } from 'bundles/recommendations-explainer/components/LazyRecommendationsExplainerDialog';
import TRexIcon from 'bundles/recommendations-explainer/components/TRexIcon';
import { readExplainerConfigurationId } from 'bundles/recommendations-explainer/utils/explainerConfigurationId';

import _t from 'i18n!nls/recommendations-explainer';

const styles = {
  button: css`
    flex-shrink: 0;
  `,
};

type IconButtonProps = Extract<React.ComponentProps<typeof IconButton>, { icon: React.ReactElement }>;

type Props = RecommendationsExplainerInput & Omit<IconButtonProps, 'icon' | 'tooltip' | 'aria-label' | 'onClick'>;

/**
 * Icon button that opens the Recommendations Explainer dialog for one recommendation collection.
 *
 * Renders nothing unless the viewer is a Courserian and recs debug mode is active. The tooltip and accessible label
 * show the recommender configuration that actually ran, and flag an experiment when it differs from `id`.
 *
 * @example
 * <RecommendationsExplainerButton id={collection.id} additionalInput={collection.additionalInput} label={title} />
 */
const RecommendationsExplainerButton = ({
  id,
  additionalInput,
  programs,
  surface,
  label,
  carouselName,
  filterSection,
  ...buttonProps
}: Props) => {
  const client = React.useContext(getApolloContext()).client;
  const enabled = useRecommendationsExplainerEnabled();
  const [isDialogOpen, setIsDialogOpen] = React.useState(false);

  if (!enabled || !isRecsDebugActive()) {
    return null;
  }

  const openDialog = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDialogOpen(true);
  };
  // Host context is client-only UI; keep it off Apollo cache lookups.
  const input: RecommendationsExplainerInput = {
    id,
    additionalInput,
    programs,
    ...pickExplainerHostContext({ surface, label, carouselName, filterSection }),
  };
  const configurationId = client ? readExplainerConfigurationId(client.cache, input) : undefined;
  const displayedId = configurationId ?? id;
  const isExperiment = configurationId !== undefined && configurationId !== id;
  const tooltip = isExperiment
    ? _t('Recommendations Explainer: #{id} (experiment on #{requestedId})', { id: displayedId, requestedId: id })
    : _t('Recommendations Explainer: #{id}', { id: displayedId });
  let ariaLabel = tooltip;
  if (label !== undefined) {
    if (isExperiment) {
      ariaLabel = _t('Recommendations Explainer: #{label} (#{id}, experiment on #{requestedId})', {
        label,
        id: displayedId,
        requestedId: id,
      });
    } else {
      ariaLabel = _t('Recommendations Explainer: #{label} (#{id})', { label, id: displayedId });
    }
  }

  return (
    <React.Fragment>
      <IconButton
        {...buttonProps}
        type="button"
        variant="ghost"
        size="small"
        // Fits inside 32px collection filter chips; default small ghost is 36px and overflows on hover.
        edgeAlign="noPadding"
        icon={<TRexIcon size="small" />}
        tooltip={tooltip}
        aria-label={ariaLabel}
        css={styles.button}
        onClick={openDialog}
      />
      {isDialogOpen && (
        <React.Suspense fallback={null}>
          <LazyRecommendationsExplainerDialog input={input} onClose={() => setIsDialogOpen(false)} />
        </React.Suspense>
      )}
    </React.Fragment>
  );
};

export default RecommendationsExplainerButton;
